/**
 * Enhanced Bank Import Page
 * Supports automatic format detection, column mapping, and categorization
 */

import { useState, useRef } from "react";
import { useAuth } from "@/_core/hooks/useAuth";
import { useCompany } from "@/contexts/CompanyContext";
import DashboardLayout from "@/components/DashboardLayout";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Progress } from "@/components/ui/progress";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Checkbox } from "@/components/ui/checkbox";
import { trpc } from "@/lib/trpc";
import { toast } from "sonner";
import {
  Upload,
  FileText,
  CheckCircle2,
  AlertCircle,
  Info,
  Sparkles,
  Download,
  Loader2,
} from "lucide-react";
import { ColumnMappingDialog } from "@/components/ColumnMappingDialog";
import {
  SmartAnalysisModal,
  type VendorAnalysis,
} from "@/components/SmartAnalysisModal";
import { VendorCorrectionDialog } from "@/components/VendorCorrectionDialog";
import { ReviewQueueContainer } from "@/components/ReviewQueue";
import PreImportRecommendationsPanel from "@/components/PreImportRecommendationsPanel";
import SmartAnalysisTuningDialog from "@/components/SmartAnalysisTuningDialog";
import PostImportLearningDialog from "@/components/PostImportLearningDialog";
import { ConfidenceBadge } from "@/components/Discovery/ConfidenceBadge";
import { PriorImportsPanel } from "@/components/CrossImportAwareness/PriorImportsPanel";
import { ImportStatusBadge } from "@/components/Discovery/ImportStatusBadge";
import { ProvenanceBadge } from "@/components/Discovery/ProvenanceBadge";
import { getLoginUrl } from "@/const";

// Import step now includes 'analyze' for pre-import recommendations
type ImportStep =
  | "upload"
  | "analyze"
  | "detect"
  | "map"
  | "preview"
  | "importing"
  | "complete"
  | "review";

// Pre-import analysis result type
interface PreImportAnalysisResult {
  fingerprint: {
    bankType: string;
    statementType: string;
    delimiter: string;
    encoding: string;
    detectedRows: number;
    confidence: number;
  };
  learnedPattern?: {
    id: number;
    timesEncountered: number;
    successRate: number;
    lastUsed: number;
    dateFormat?: string;
    vendorExtractionPattern?: string;
  };
  recommendations: Array<{
    id: number;
    type: "format" | "vendor" | "pattern" | "attention" | "learning";
    priority: "high" | "medium" | "low";
    title: string;
    description: string;
    actionable: boolean;
    measurementKey?: string;
    metadata?: Record<string, any>;
  }>;
  previewStats: {
    estimatedTransactions: number;
    estimatedVendors: number;
    dateRange?: { from: string; to: string };
    hasBankPrefixes: boolean;
    prefixExamples: string[];
  };
  sessionId?: number;
}

interface DetectionResult {
  delimiter: "," | ";" | "\t" | "|";
  encoding: string;
  headerRows: number;
  dataStartRow: number;
  totalRows: number;
  bankType: string;
  statementType: string;
  confidence: number;
  metadata?: {
    accountNumber?: string;
    dateFrom?: string;
    dateTo?: string;
    currency?: string;
    documentType?: string;
    year?: number;
    period?: string;
  };
}

interface ColumnMapping {
  date?: number;
  description?: number;
  amount?: number;
  credit?: number;
  debit?: number;
  balance?: number;
  reference?: number;
  bookingDate?: number;
  purchaseDate?: number;
  invoicingPeriod?: number;
}

interface ParsedTransaction {
  date: number;
  description: string;
  amount: number;
  type: "credit" | "debit";
  currency: string;
  category?: string;
  accountCode?: string;
  accountName?: string;
  vatRate?: number;
  confidence?: number;
  isDuplicate?: boolean;
  duplicateCount?: number;
  rowNumber: number;
  reference?: string;
  balance?: number;
}

export default function EnhancedBankImport() {
  const { user, isAuthenticated, loading: authLoading } = useAuth();
  const { selectedCompanyId } = useCompany();

  // File upload state
  const [files, setFiles] = useState<File[]>([]);
  const [dragActive, setDragActive] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Batch processing state
  const [fileStatuses, setFileStatuses] = useState<
    Map<
      string,
      {
        status:
          | "pending"
          | "detecting"
          | "parsing"
          | "categorizing"
          | "complete"
          | "error";
        progress: number;
        error?: string;
        transactionCount?: number;
      }
    >
  >(new Map());

  // Store current CSV content for mapping dialog and reparsing
  const [csvContent, setCsvContent] = useState<string | null>(null);

  // Import flow state
  const [step, setStep] = useState<ImportStep>("upload");
  const [detection, setDetection] = useState<DetectionResult | null>(null);
  const [headerRow, setHeaderRow] = useState<string[]>([]);
  const [previewRows, setPreviewRows] = useState<string[][]>([]);
  const [suggestedMapping, setSuggestedMapping] = useState<ColumnMapping>({});
  const [finalMapping, setFinalMapping] = useState<ColumnMapping>({});
  const [parsedTransactions, setParsedTransactions] = useState<
    ParsedTransaction[]
  >([]);
  const [categorizedTransactions, setCategorizedTransactions] = useState<any[]>(
    []
  );
  const [selectedTransactions, setSelectedTransactions] = useState<Set<number>>(
    new Set()
  );

  // Smart Analysis state
  const [vendorAnalyses, setVendorAnalyses] = useState<VendorAnalysis[]>([]);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [showAnalysisModal, setShowAnalysisModal] = useState(false);
  const [showCorrectionDialog, setShowCorrectionDialog] = useState(false);
  const [correctionTarget, setCorrectionTarget] =
    useState<VendorAnalysis | null>(null);

  // Intelligence Import state
  const [preImportAnalysis, setPreImportAnalysis] =
    useState<PreImportAnalysisResult | null>(null);
  const [showTuningDialog, setShowTuningDialog] = useState(false);
  const [showLearningDialog, setShowLearningDialog] = useState(false);
  const [sessionId, setSessionId] = useState<number | undefined>(undefined);
  const [togglePreferences, setTogglePreferences] = useState<
    Record<string, boolean>
  >({});
  const [extractedDescriptions, setExtractedDescriptions] = useState<string[]>(
    []
  );

  // UI state
  const [showMappingDialog, setShowMappingDialog] = useState(false);
  const [importProgress, setImportProgress] = useState(0);

  // Import result state for review
  const [importResult, setImportResult] = useState<{
    imported: number;
    skipped: number;
  } | null>(null);

  // Cross-import context for intelligence sharing
  const {
    data: crossImportContext,
    refetch: refetchCrossImport,
    isLoading: isLoadingCrossImport,
  } = trpc.crossImport.getContext.useQuery(
    { companyId: selectedCompanyId ?? 0, currentImportPath: "enhanced" },
    { enabled: !!selectedCompanyId }
  );

  // tRPC mutations
  const preImportAnalysisMutation =
    trpc.enhancedImport.preImportAnalysis.useMutation({
      onSuccess: data => {
        setPreImportAnalysis(data);
        setSessionId(data.sessionId);
        setStep("analyze");
        toast.success("Pre-import analysis complete");
      },
      onError: (error: any) => {
        console.warn(
          "[PreImportAnalysis] Failed, continuing without recommendations:",
          error
        );
        // Continue without pre-import analysis
        setStep("detect");
      },
    });

  const detectFormatMutation = trpc.enhancedImport.detectFormat.useMutation({
    onSuccess: (data, variables) => {
      setDetection(data.detection);
      setHeaderRow(data.headerRow);
      setPreviewRows(data.previewRows);
      setSuggestedMapping(data.suggestedMapping);

      if (data.validation.valid) {
        // Auto-proceed if mapping is valid
        setStep("preview");
        parseWithMapping(
          data.suggestedMapping,
          data.detection,
          variables.csvContent
        );
      } else {
        // Show mapping dialog for user confirmation
        setStep("map");
        setShowMappingDialog(true);
      }

      toast.success(
        `Detected ${data.detection.bankType} ${data.detection.statementType} (${Math.round(data.detection.confidence * 100)}% confidence)`
      );
    },
    onError: (error: any) => {
      const errorCode = error.data?.code || "UNKNOWN_ERROR";
      const userMessage = error.data?.userMessage || error.message;
      const recoveryHints = error.data?.recoveryHints || [];

      toast.error(
        <div>
          <div className="font-semibold">{userMessage}</div>
          {errorCode !== "UNKNOWN_ERROR" && (
            <div className="text-xs opacity-80 mt-1">Error: {errorCode}</div>
          )}
        </div>
      );

      // Log technical details for debugging
      if (error.data?.technicalDetails) {
        console.error("[Format Detection Error]", error.data.technicalDetails);
      }

      setStep("upload");
    },
  });

  const parseWithMappingMutation =
    trpc.enhancedImport.parseWithMapping.useMutation({
      onSuccess: data => {
        setParsedTransactions(data.transactions);
        setStep("preview");
        toast.success(`Parsed ${data.transactions.length} transactions`);

        // Auto-categorize
        categorizeParsedTransactions(data.transactions);
      },
      onError: (error: any) => {
        const errorCode = error.data?.code || "UNKNOWN_ERROR";
        const userMessage = error.data?.userMessage || error.message;

        toast.error(
          <div>
            <div className="font-semibold">{userMessage}</div>
            {errorCode !== "UNKNOWN_ERROR" && (
              <div className="text-xs opacity-80 mt-1">Error: {errorCode}</div>
            )}
          </div>
        );

        if (error.data?.technicalDetails) {
          console.error("[Parsing Error]", error.data.technicalDetails);
        }
      },
    });

  const categorizeTransactionsMutation =
    trpc.enhancedImport.categorizeTransactions.useMutation({
      onSuccess: data => {
        setCategorizedTransactions(data);
        // Select all non-duplicates by default
        const nonDuplicates = new Set(
          data.map((_, idx) => idx).filter(idx => !data[idx].isDuplicate)
        );
        setSelectedTransactions(nonDuplicates);
        toast.success("Transactions categorized automatically");
      },
      onError: (error: any) => {
        const errorCode = error.data?.code || "UNKNOWN_ERROR";
        const userMessage = error.data?.userMessage || error.message;

        // Categorization errors are non-fatal, show as warning
        toast.warning(
          <div>
            <div className="font-semibold">{userMessage}</div>
            <div className="text-xs mt-1">
              Transactions will be imported as uncategorized
            </div>
          </div>
        );

        if (error.data?.technicalDetails) {
          console.error("[Categorization Error]", error.data.technicalDetails);
        }

        // Fallback: Use parsed transactions as uncategorized
        setCategorizedTransactions(
          parsedTransactions.map(t => ({
            date: t.date,
            description: t.description,
            amount: t.amount,
            type: t.type,
            currency: t.currency,
            reference: t.reference,
            balance: t.balance,
            rowNumber: t.rowNumber,
            category: "Uncategorized",
            accountCode: "1020",
            accountName: "Bank Account",
          }))
        );

        // Select all (default behavior)
        const allIndices = new Set(parsedTransactions.map((_, idx) => idx));
        setSelectedTransactions(allIndices);
      },
    });

  const importTransactionsMutation =
    trpc.enhancedImport.importTransactions.useMutation({
      onSuccess: data => {
        setImportResult({ imported: data.imported, skipped: data.skipped });
        setStep("complete");
        toast.success(
          `Imported ${data.imported} transactions (${data.skipped} duplicates skipped)`
        );
        // Show learning dialog after successful import
        if (sessionId) {
          setShowLearningDialog(true);
        }
      },
      onError: (error: any) => {
        const errorCode = error.data?.code || "UNKNOWN_ERROR";
        const userMessage = error.data?.userMessage || error.message;
        const recoveryHints = error.data?.recoveryHints || [];

        toast.error(
          <div>
            <div className="font-semibold">{userMessage}</div>
            {errorCode !== "UNKNOWN_ERROR" && (
              <div className="text-xs opacity-80 mt-1">Error: {errorCode}</div>
            )}
            {recoveryHints.length > 0 && (
              <ul className="text-xs mt-2 space-y-1 list-disc list-inside">
                {recoveryHints.slice(0, 2).map((hint: string, idx: number) => (
                  <li key={idx}>{hint}</li>
                ))}
              </ul>
            )}
          </div>
        );

        if (error.data?.technicalDetails) {
          console.error("[Import Error]", error.data.technicalDetails);
        }

        setStep("preview");
      },
    });

  const parseFileWithLLMMutation =
    trpc.enhancedImport.parseFileWithLLM.useMutation({
      onSuccess: data => {
        setParsedTransactions(data.transactions);

        // Update detection state with classification metadata for UI
        setDetection({
          delimiter: "," as any, // Dummy for LLM
          encoding: "utf-8",
          headerRows: 0,
          dataStartRow: 0,
          totalRows: data.transactions.length,
          bankType: data.metadata.bankName || "Unknown",
          statementType: data.metadata.documentType || "unknown",
          confidence: data.metadata.confidence,
          metadata: {
            accountNumber: data.metadata.accountNumber,
            currency: data.metadata.currency,
            documentType: data.metadata.documentType,
            year: data.metadata.year,
            period: data.metadata.period,
          },
        });

        setStep("preview");
        toast.success(
          `Parsed ${data.transactions.length} transactions via LLM`
        );

        // Auto-categorize
        categorizeParsedTransactions(data.transactions);
      },
      onError: (error: any) => {
        const errorCode = error.data?.code || "UNKNOWN_ERROR";
        const userMessage = error.data?.userMessage || error.message;

        toast.error(
          <div>
            <div className="font-semibold">{userMessage}</div>
            {errorCode !== "UNKNOWN_ERROR" && (
              <div className="text-xs opacity-80 mt-1">Error: {errorCode}</div>
            )}
          </div>
        );

        if (error.data?.technicalDetails) {
          console.error("[LLM Parsing Error]", error.data.technicalDetails);
        }
      },
    });

  // LLM-based CSV parsing (for complex formats like PostFinance with metadata headers)
  const parseWithLLMMutation = trpc.enhancedImport.parseFileWithLLM.useMutation(
    {
      onError: (error: any) => {
        console.error("[LLM CSV Parsing Error]", error.message);
      },
    }
  );

  const smartIdentifyMutation =
    trpc.llmVendor.smartIdentifyVendor.useMutation();
  const upsertVendorMutation =
    trpc.vendorIntelligence.upsertVendor.useMutation();

  // File handling
  const handleDrag = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      handleFilesSelect(Array.from(e.dataTransfer.files));
    }
  };

  const handleFilesSelect = async (selectedFiles: File[]) => {
    // Allow CSV and PDF
    const validFiles = selectedFiles.filter(
      f =>
        f.name.endsWith(".csv") ||
        f.name.endsWith(".pdf") ||
        f.name.endsWith(".txt")
    );

    if (validFiles.length === 0) {
      toast.error("Please select at least one CSV or PDF file");
      return;
    }

    if (validFiles.length !== selectedFiles.length) {
      toast.warning(
        `Skipped ${selectedFiles.length - validFiles.length} unsupported files`
      );
    }

    setFiles(validFiles);

    // Initialize status for each file
    const initialStatuses = new Map();
    validFiles.forEach(file => {
      initialStatuses.set(file.name, {
        status: "pending" as const,
        progress: 0,
      });
    });
    setFileStatuses(initialStatuses);

    // For CSV files, run pre-import analysis first
    const firstCsvFile = validFiles.find(f => f.name.endsWith(".csv"));
    if (firstCsvFile && selectedCompanyId) {
      const content = await readFileAsText(firstCsvFile);
      setCsvContent(content);

      // Extract descriptions for tuning preview (from first few lines)
      const lines = content.split("\n").slice(0, 50);
      setExtractedDescriptions(lines.filter(l => l.trim().length > 10));

      // Run pre-import analysis
      setStep("analyze");
      try {
        await preImportAnalysisMutation.mutateAsync({
          companyId: selectedCompanyId,
          csvContent: content,
          fileName: firstCsvFile.name,
        });
      } catch (error) {
        // Continue without pre-import analysis
        setStep("detect");
        await processFilesInBatch(validFiles);
      }
    } else if (selectedCompanyId) {
      // For non-CSV files, skip pre-import analysis
      setStep("detect");
      await processFilesInBatch(validFiles);
    }
  };

  // Proceed from analysis step to full processing
  const proceedFromAnalysis = async () => {
    setStep("detect");
    await processFilesInBatch(files);
  };

  const processFilesInBatch = async (filesToProcess: File[]) => {
    const allTransactions: ParsedTransaction[] = [];

    // Process each file
    for (const file of filesToProcess) {
      try {
        const isCsv = file.name.endsWith(".csv");

        if (isCsv) {
          // CSV Logic with LLM fallback for complex formats
          setFileStatuses(prev =>
            new Map(prev).set(file.name, {
              status: "detecting",
              progress: 10,
            })
          );

          // Read file content
          const content = await readFileAsText(file);
          setCsvContent(content);

          let parseResult: { transactions: ParsedTransaction[] } | null = null;
          let usedLLM = false;

          // Try regex-based detection first
          try {
            setFileStatuses(prev =>
              new Map(prev).set(file.name, {
                status: "detecting",
                progress: 25,
              })
            );

            const detectionResult = await detectFormatMutation.mutateAsync({
              companyId: selectedCompanyId!,
              csvContent: content,
            });

            setFileStatuses(prev =>
              new Map(prev).set(file.name, {
                status: "parsing",
                progress: 50,
              })
            );

            // Parse with mapping
            parseResult = await parseWithMappingMutation.mutateAsync({
              companyId: selectedCompanyId!,
              csvContent: content,
              delimiter: detectionResult.detection.delimiter,
              mapping: detectionResult.suggestedMapping,
              dataStartRow: detectionResult.detection.dataStartRow,
              metadata: detectionResult.detection.metadata,
            });

            // Check if regex parsing found transactions
            if (parseResult.transactions.length === 0) {
              console.log(
                `[Enhanced Import] Regex parsing found 0 transactions for ${file.name}, falling back to LLM`
              );
              parseResult = null; // Trigger LLM fallback
            }
          } catch (regexError: any) {
            console.log(
              `[Enhanced Import] Regex detection/parsing failed for ${file.name}, falling back to LLM:`,
              regexError.message
            );
            // Continue to LLM fallback
          }

          // Fallback to LLM if regex approach failed or found 0 transactions
          if (!parseResult || parseResult.transactions.length === 0) {
            usedLLM = true;
            setFileStatuses(prev =>
              new Map(prev).set(file.name, {
                status: "parsing",
                progress: 60,
              })
            );

            toast.info(
              `Using AI parsing for ${file.name} (complex format detected)`
            );

            // Use LLM-based parsing for CSV (convert to base64)
            const base64CsvContent = btoa(
              unescape(encodeURIComponent(content))
            );
            const llmResult = await parseWithLLMMutation.mutateAsync({
              companyId: selectedCompanyId!,
              base64Content: base64CsvContent,
              fileName: file.name,
              mimeType: "text/csv",
            });

            parseResult = { transactions: llmResult.transactions };

            // Update detection state from LLM metadata
            setDetection({
              delimiter: "," as const,
              encoding: "utf-8",
              headerRows: 0,
              dataStartRow: 0,
              totalRows: llmResult.transactions.length,
              bankType: llmResult.metadata.bankName || "Unknown",
              statementType:
                llmResult.metadata.documentType || "bank_statement",
              confidence: llmResult.metadata.confidence,
              metadata: {
                accountNumber: llmResult.metadata.accountNumber,
                currency: llmResult.metadata.currency,
                documentType: llmResult.metadata.documentType,
                year: llmResult.metadata.year,
                period: llmResult.metadata.period,
              },
            });
          }

          allTransactions.push(...parseResult.transactions);

          setFileStatuses(prev =>
            new Map(prev).set(file.name, {
              status: "complete",
              progress: 100,
              transactionCount: parseResult.transactions.length,
            })
          );

          if (usedLLM && parseResult.transactions.length > 0) {
            toast.success(
              `AI parsed ${parseResult.transactions.length} transactions from ${file.name}`
            );
          }
        } else {
          // New LLM Logic for PDF/Text
          setFileStatuses(prev =>
            new Map(prev).set(file.name, {
              status: "detecting", // Reusing status for UI simplicity
              progress: 20,
            })
          );

          // Read file as base64
          const base64Content = await readFileAsBase64(file);

          setFileStatuses(prev =>
            new Map(prev).set(file.name, {
              status: "parsing",
              progress: 40,
            })
          );

          const llmResult = await parseFileWithLLMMutation.mutateAsync({
            companyId: selectedCompanyId!,
            base64Content,
            fileName: file.name,
            mimeType:
              file.type ||
              (file.name.endsWith(".pdf") ? "application/pdf" : "text/plain"),
          });

          allTransactions.push(...llmResult.transactions);

          setFileStatuses(prev =>
            new Map(prev).set(file.name, {
              status: "complete",
              progress: 100,
              transactionCount: llmResult.transactions.length,
            })
          );
        }
      } catch (error: any) {
        const errorMessage =
          error.data?.userMessage || error.message || "Unknown error";
        const errorCode = error.data?.code;

        setFileStatuses(prev =>
          new Map(prev).set(file.name, {
            status: "error",
            progress: 0,
            error: errorCode ? `${errorCode}: ${errorMessage}` : errorMessage,
          })
        );

        toast.error(
          <div>
            <div className="font-semibold">Failed to process {file.name}</div>
            <div className="text-xs mt-1">{errorMessage}</div>
          </div>
        );

        if (error.data?.technicalDetails) {
          console.error(
            `[File Processing Error: ${file.name}]`,
            error.data.technicalDetails
          );
        }
      }
    }

    // Set aggregated transactions
    setParsedTransactions(allTransactions);
    setStep("preview");
    toast.success(
      `Processed ${filesToProcess.length} files, found ${allTransactions.length} transactions`
    );

    // Auto-categorize all transactions
    if (allTransactions.length > 0) {
      categorizeParsedTransactions(allTransactions);
    }
  };

  const readFileAsText = (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = e => resolve(e.target?.result as string);
      reader.onerror = reject;
      reader.readAsText(file);
    });
  };

  const readFileAsBase64 = (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = e => {
        const result = e.target?.result as string;
        // Remove data URL prefix (e.g., "data:application/pdf;base64,")
        const base64 = result.split(",")[1];
        resolve(base64);
      };
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  };

  const parseWithMapping = (
    mapping: ColumnMapping,
    detectionResult: DetectionResult,
    content?: string
  ) => {
    const contentToUse = content || csvContent;
    if (!selectedCompanyId || !contentToUse) return;

    setFinalMapping(mapping);
    parseWithMappingMutation.mutate({
      companyId: selectedCompanyId,
      csvContent: contentToUse,
      delimiter: detectionResult.delimiter,
      mapping,
      dataStartRow: detectionResult.dataStartRow,
      metadata: detectionResult.metadata,
    });
  };

  const categorizeParsedTransactions = (transactions: ParsedTransaction[]) => {
    if (!selectedCompanyId) return;

    categorizeTransactionsMutation.mutate({
      companyId: selectedCompanyId,
      transactions: transactions.map(t => ({
        date: t.date,
        description: t.description,
        amount: t.amount,
        type: t.type,
        currency: t.currency,
        reference: t.reference,
        balance: t.balance,
        rowNumber: t.rowNumber,
      })),
    });
  };

  const handleImport = () => {
    if (!selectedCompanyId || categorizedTransactions.length === 0) return;

    const selectedTxns = categorizedTransactions.filter((_, idx) =>
      selectedTransactions.has(idx)
    );

    if (selectedTxns.length === 0) {
      toast.error("Please select at least one transaction to import");
      return;
    }

    setStep("importing");
    importTransactionsMutation.mutate({
      companyId: selectedCompanyId,
      transactions: selectedTxns,
      skipDuplicates: true,
    });
  };

  const toggleTransaction = (index: number) => {
    setSelectedTransactions(prev => {
      const next = new Set(prev);
      if (next.has(index)) {
        next.delete(index);
      } else {
        next.add(index);
      }
      return next;
    });
  };

  const toggleAll = () => {
    if (selectedTransactions.size === categorizedTransactions.length) {
      setSelectedTransactions(new Set());
    } else {
      setSelectedTransactions(
        new Set(categorizedTransactions.map((_, idx) => idx))
      );
    }
  };

  const resetImport = () => {
    setFiles([]);
    setFileStatuses(new Map());
    setStep("upload");
    setDetection(null);
    setParsedTransactions([]);
    setCategorizedTransactions([]);
    setSelectedTransactions(new Set());
    setVendorAnalyses([]);
    setIsAnalyzing(false);
    setShowAnalysisModal(false);
    setImportResult(null);
  };

  // Smart Analysis handler
  const handleSmartAnalysis = async () => {
    if (!selectedCompanyId) {
      toast.error("Please select a company first");
      return;
    }

    setIsAnalyzing(true);
    const analyses: VendorAnalysis[] = [];

    try {
      // Analyze selected transactions
      const selectedTxns = categorizedTransactions.filter((_, idx) =>
        selectedTransactions.has(idx)
      );

      toast.info(`Analyzing ${selectedTxns.length} transactions with AI...`);

      for (let i = 0; i < selectedTxns.length; i++) {
        const txn = selectedTxns[i];
        const txnIndex = categorizedTransactions.indexOf(txn);

        try {
          const result = await smartIdentifyMutation.mutateAsync({
            companyId: selectedCompanyId,
            description: txn.description,
            amount: txn.amount,
            date: txn.date,
            bankName: detection?.bankType || "Unknown",
          });

          analyses.push({
            transactionIndex: txnIndex,
            description: txn.description,
            amount: txn.amount,
            date: new Date(txn.date).toLocaleDateString("de-CH"),
            llmExtraction: result.llmExtraction,
            enrichment: result.enrichment ?? null,
            recommendation: result.recommendation,
          });
        } catch (error) {
          console.error(`Failed to analyze transaction ${i}:`, error);
        }
      }

      setVendorAnalyses(analyses);
      setShowAnalysisModal(true);
      toast.success(`Analyzed ${analyses.length} transactions successfully`);
    } catch (error) {
      toast.error("Smart analysis failed");
      console.error(error);
    } finally {
      setIsAnalyzing(false);
    }
  };

  const handleApproveVendors = async (selectedIndices: number[]) => {
    if (!selectedCompanyId) return;

    try {
      const selectedAnalyses = vendorAnalyses.filter(a =>
        selectedIndices.includes(a.transactionIndex)
      );

      // Create vendors
      for (const analysis of selectedAnalyses) {
        await upsertVendorMutation.mutateAsync({
          companyId: selectedCompanyId,
          ...analysis.recommendation.vendorData,
        });
      }

      toast.success(`Created ${selectedAnalyses.length} vendors successfully`);
      setShowAnalysisModal(false);
      setVendorAnalyses([]);
    } catch (error) {
      toast.error("Failed to create vendors");
      console.error(error);
    }
  };

  // Auth check
  if (authLoading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
      </DashboardLayout>
    );
  }

  if (!isAuthenticated) {
    return (
      <DashboardLayout>
        <Card>
          <CardHeader>
            <CardTitle>Authentication Required</CardTitle>
            <CardDescription>
              Please log in to import bank statements
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Button asChild>
              <a href={getLoginUrl()}>Log In</a>
            </Button>
          </CardContent>
        </Card>
      </DashboardLayout>
    );
  }

  if (!selectedCompanyId) {
    return (
      <DashboardLayout>
        <Card>
          <CardHeader>
            <CardTitle>No Company Selected</CardTitle>
            <CardDescription>
              Please select a company to import transactions
            </CardDescription>
          </CardHeader>
        </Card>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6">
        {/* Header */}
        <div>
          <h1 className="text-3xl font-bold">Enhanced Bank Import</h1>
          <p className="text-muted-foreground mt-2">
            Import transactions from any Swiss bank with automatic format
            detection and categorization
          </p>
        </div>

        {/* Cross-Import Intelligence Panel */}
        {selectedCompanyId && (
          <PriorImportsPanel
            currentImportPath="enhanced"
            companyId={selectedCompanyId}
            priorDocuments={crossImportContext?.priorDocuments?.map(d => ({
              ...d,
              classification: d.classification ?? "unknown",
            }))}
            knownVendors={crossImportContext?.knownVendors}
            knownCustomers={crossImportContext?.knownCustomers}
            learnedPatterns={crossImportContext?.learnedPatterns}
            isLoading={isLoadingCrossImport}
            onRefresh={refetchCrossImport}
          />
        )}

        {/* Document Registry: Discovery-origin bank statements */}
        {crossImportContext?.priorDocuments &&
          crossImportContext.priorDocuments.filter(
            (d: any) => d.classification === "bank_statement"
          ).length > 0 && (
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-sm font-medium">
                  Document Registry
                </CardTitle>
                <CardDescription>
                  Bank statements found via document intelligence
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {crossImportContext.priorDocuments
                    .filter((d: any) => d.classification === "bank_statement")
                    .slice(0, 5)
                    .map((doc: any) => (
                      <div
                        key={doc.id}
                        className="flex items-center justify-between rounded-md border p-2 text-sm"
                      >
                        <div className="flex items-center gap-2 min-w-0">
                          <FileText className="h-4 w-4 text-muted-foreground shrink-0" />
                          <span className="truncate">{doc.fileName}</span>
                        </div>
                        <div className="flex items-center gap-2 shrink-0">
                          <ImportStatusBadge status={doc.importStatus} />
                          <ProvenanceBadge provenance={doc.provenance} />
                        </div>
                      </div>
                    ))}
                </div>
              </CardContent>
            </Card>
          )}

        {/* Progress Steps */}
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              {["Upload", "Detect", "Map", "Preview", "Import", "Review"].map(
                (label, idx) => {
                  const allSteps = [
                    "upload",
                    "detect",
                    "map",
                    "preview",
                    "importing",
                    "complete",
                    "review",
                  ];
                  const currentStepIndex = allSteps.indexOf(step);
                  // Map display steps to workflow steps
                  const displayToWorkflow = [0, 1, 2, 3, 4, 6]; // Index mapping
                  const stepIndex =
                    displayToWorkflow.indexOf(currentStepIndex) !== -1
                      ? displayToWorkflow.indexOf(currentStepIndex)
                      : step === "complete"
                        ? 4
                        : 0;
                  const isActive = idx <= stepIndex;
                  const isCurrent =
                    idx === stepIndex || (step === "review" && idx === 5);

                  return (
                    <div key={label} className="flex items-center">
                      <div
                        className={`flex items-center gap-2 ${isActive ? "text-primary" : "text-muted-foreground"}`}
                      >
                        <div
                          className={`w-8 h-8 rounded-full flex items-center justify-center border-2 ${
                            isCurrent
                              ? "border-primary bg-primary text-primary-foreground"
                              : isActive
                                ? "border-primary"
                                : "border-muted"
                          }`}
                        >
                          {isActive && !isCurrent ? (
                            <CheckCircle2 className="h-5 w-5" />
                          ) : (
                            idx + 1
                          )}
                        </div>
                        <span className="font-medium text-sm">{label}</span>
                      </div>
                      {idx < 5 && (
                        <div
                          className={`w-12 h-0.5 mx-2 ${isActive ? "bg-primary" : "bg-muted"}`}
                        />
                      )}
                    </div>
                  );
                }
              )}
            </div>
          </CardContent>
        </Card>

        {/* Upload Step */}
        {step === "upload" && (
          <Card>
            <CardHeader>
              <CardTitle>Upload Bank Statement</CardTitle>
              <CardDescription>
                Supports PostFinance, UBS, Credit Suisse (PDF or CSV).
                Intelligent LLM parsing for PDFs.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div
                className={`border-2 border-dashed rounded-lg p-12 text-center transition-colors ${
                  dragActive ? "border-primary bg-primary/5" : "border-muted"
                }`}
                onDragEnter={handleDrag}
                onDragLeave={handleDrag}
                onDragOver={handleDrag}
                onDrop={handleDrop}
              >
                <Upload className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                <p className="text-lg font-medium mb-2">
                  Drop CSV or PDF files here, or click to browse
                </p>
                <p className="text-sm text-muted-foreground mb-4">
                  Select multiple files for batch processing • Supports PDF, CSV
                </p>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".csv,.pdf,.txt"
                  multiple
                  onChange={e =>
                    e.target.files &&
                    handleFilesSelect(Array.from(e.target.files))
                  }
                  className="hidden"
                  title="Upload files"
                  aria-label="Upload CSV or PDF files"
                />
                <Button onClick={() => fileInputRef.current?.click()}>
                  Select Files
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Pre-Import Analysis Step */}
        {step === "analyze" && (
          <PreImportRecommendationsPanel
            analysis={preImportAnalysis}
            isLoading={preImportAnalysisMutation.isPending}
            onOpenTuning={() => setShowTuningDialog(true)}
            onProceed={proceedFromAnalysis}
          />
        )}

        {/* Detection Step - Batch Processing */}
        {step === "detect" && files.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle>
                Processing {files.length} File{files.length > 1 ? "s" : ""}...
              </CardTitle>
              <CardDescription>
                Detecting formats (CSV) or analyzing with AI (PDF), then
                categorizing
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {files.map(file => {
                  const status = fileStatuses.get(file.name);
                  if (!status) return null;

                  const statusIcons = {
                    pending: (
                      <FileText className="h-5 w-5 text-muted-foreground" />
                    ),
                    detecting: (
                      <Loader2 className="h-5 w-5 animate-spin text-[var(--info)]" />
                    ),
                    parsing: (
                      <Loader2 className="h-5 w-5 animate-spin text-[var(--info)]" />
                    ),
                    categorizing: (
                      <Sparkles className="h-5 w-5 animate-pulse text-[var(--warning)]" />
                    ),
                    complete: (
                      <CheckCircle2 className="h-5 w-5 text-[var(--success)]" />
                    ),
                    error: <AlertCircle className="h-5 w-5 text-destructive" />,
                  };

                  const statusLabels = {
                    pending: "Pending",
                    detecting: "Detecting format",
                    parsing: "Parsing transactions",
                    categorizing: "Categorizing",
                    complete: `Complete (${status.transactionCount || 0} transactions)`,
                    error: status.error || "Failed",
                  };

                  return (
                    <div key={file.name} className="space-y-2">
                      <div className="flex items-center gap-3">
                        {statusIcons[status.status]}
                        <div className="flex-1">
                          <p className="font-medium text-sm">{file.name}</p>
                          <p className="text-xs text-muted-foreground">
                            {statusLabels[status.status]}
                          </p>
                        </div>
                        <Badge
                          variant={
                            status.status === "error"
                              ? "destructive"
                              : status.status === "complete"
                                ? "default"
                                : "secondary"
                          }
                        >
                          {status.progress}%
                        </Badge>
                      </div>
                      <Progress value={status.progress} className="h-2" />
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Preview Step */}
        {step === "preview" && (
          <>
            {categorizedTransactions.length === 0 &&
            parsedTransactions.length === 0 ? (
              // No transactions - likely stale state, show reset option
              <Card>
                <CardContent className="py-12 text-center">
                  <AlertCircle className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                  <h3 className="text-lg font-medium">No Transactions Found</h3>
                  <p className="text-muted-foreground mb-4">
                    Please upload a bank statement to get started.
                  </p>
                  <Button onClick={resetImport}>
                    <Upload className="mr-2 h-4 w-4" />
                    Upload Files
                  </Button>
                </CardContent>
              </Card>
            ) : categorizedTransactions.length === 0 &&
              parsedTransactions.length > 0 ? (
              <Card>
                <CardContent className="py-12 text-center">
                  <Loader2 className="h-12 w-12 animate-spin mx-auto text-primary mb-4" />
                  <h3 className="text-lg font-medium">Preparing Preview...</h3>
                  <p className="text-muted-foreground">
                    Categorizing {parsedTransactions.length} transactions
                  </p>
                </CardContent>
              </Card>
            ) : categorizedTransactions.length > 0 ? (
              <Card>
                <CardHeader>
                  <CardTitle>Review Transactions</CardTitle>
                  <CardDescription>
                    {categorizedTransactions.length} transactions parsed •{" "}
                    {selectedTransactions.size} selected
                    {detection?.metadata && (
                      <div className="mt-2 flex flex-col gap-1 text-xs text-muted-foreground">
                        <div className="flex gap-4">
                          {detection.metadata.documentType && (
                            <span className="font-medium text-[var(--info)] uppercase">
                              DOC:{" "}
                              {detection.metadata.documentType.replace(
                                "_",
                                " "
                              )}
                            </span>
                          )}
                          {detection.metadata.year && (
                            <span className="font-medium text-[var(--info)]">
                              YEAR: {detection.metadata.year}
                            </span>
                          )}
                          {detection.metadata.accountNumber && (
                            <span>
                              Account: {detection.metadata.accountNumber}
                            </span>
                          )}
                        </div>
                        <div className="flex gap-4">
                          {detection.metadata.dateFrom &&
                            detection.metadata.dateTo && (
                              <span>
                                Period: {detection.metadata.dateFrom} -{" "}
                                {detection.metadata.dateTo}
                              </span>
                            )}
                          {detection.metadata.period &&
                            !detection.metadata.dateFrom && (
                              <span>Period: {detection.metadata.period}</span>
                            )}
                          {detection.metadata.currency && (
                            <span>Currency: {detection.metadata.currency}</span>
                          )}
                        </div>
                      </div>
                    )}
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <Button variant="outline" size="sm" onClick={toggleAll}>
                        {selectedTransactions.size ===
                        categorizedTransactions.length
                          ? "Deselect All"
                          : "Select All"}
                      </Button>
                      <div className="flex gap-2">
                        <Button variant="outline" onClick={resetImport}>
                          Cancel
                        </Button>
                        <Button
                          variant="outline"
                          onClick={handleSmartAnalysis}
                          disabled={
                            selectedTransactions.size === 0 || isAnalyzing
                          }
                        >
                          {isAnalyzing ? (
                            <>
                              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                              Analyzing...
                            </>
                          ) : (
                            <>
                              <Sparkles className="mr-2 h-4 w-4" />
                              Smart Analysis
                            </>
                          )}
                        </Button>
                        <Button
                          onClick={handleImport}
                          disabled={selectedTransactions.size === 0}
                        >
                          <Download className="mr-2 h-4 w-4" />
                          Import {selectedTransactions.size} Transaction
                          {selectedTransactions.size !== 1 ? "s" : ""}
                        </Button>
                      </div>
                    </div>

                    <div className="border rounded-md">
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead className="w-[50px]">
                              <Checkbox
                                checked={
                                  selectedTransactions.size ===
                                  categorizedTransactions.length
                                }
                                onCheckedChange={toggleAll}
                              />
                            </TableHead>
                            <TableHead>Date</TableHead>
                            <TableHead>Description</TableHead>
                            <TableHead>Reference</TableHead>
                            <TableHead>Category</TableHead>
                            <TableHead>Confidence</TableHead>
                            <TableHead className="text-right">Amount</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {categorizedTransactions.map((txn, idx) => (
                            <TableRow
                              key={idx}
                              className={
                                !selectedTransactions.has(idx)
                                  ? "opacity-50"
                                  : ""
                              }
                            >
                              <TableCell>
                                <Checkbox
                                  checked={selectedTransactions.has(idx)}
                                  onCheckedChange={() => toggleTransaction(idx)}
                                  disabled={txn.isDuplicate}
                                />
                              </TableCell>
                              <TableCell className="font-mono text-sm">
                                {new Date(txn.date).toLocaleDateString("de-CH")}
                              </TableCell>
                              <TableCell className="max-w-xs truncate">
                                <div className="font-medium">
                                  {txn.description}
                                </div>
                                {txn.isDuplicate && (
                                  <Badge
                                    variant="secondary"
                                    className="mt-1 text-xs"
                                  >
                                    Duplicate
                                  </Badge>
                                )}
                              </TableCell>
                              <TableCell className="text-sm text-muted-foreground">
                                {txn.reference || "-"}
                              </TableCell>
                              <TableCell>
                                <div className="flex flex-col">
                                  <span className="font-medium text-sm">
                                    {txn.category || "Uncategorized"}
                                  </span>
                                  <span className="text-xs text-muted-foreground">
                                    {txn.accountName} ({txn.accountCode})
                                  </span>
                                </div>
                              </TableCell>
                              <TableCell>
                                {txn.confidence !== undefined ? (
                                  <ConfidenceBadge
                                    confidence={txn.confidence}
                                    size="sm"
                                  />
                                ) : (
                                  <span className="text-xs text-muted-foreground">
                                    -
                                  </span>
                                )}
                              </TableCell>
                              <TableCell
                                className={`text-right font-mono ${
                                  txn.type === "credit"
                                    ? "text-[var(--success)]"
                                    : "text-destructive"
                                }`}
                              >
                                {txn.type === "credit" ? "+" : "-"}
                                {new Intl.NumberFormat("de-CH", {
                                  minimumFractionDigits: 2,
                                }).format(txn.amount / 100)}
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ) : null}
          </>
        )}

        {/* Importing Step */}
        {step === "importing" && (
          <Card>
            <CardHeader>
              <CardTitle>Importing Transactions...</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="flex items-center gap-4">
                  <Loader2 className="h-8 w-8 animate-spin" />
                  <div className="flex-1">
                    <p className="font-medium">Saving to database</p>
                    <p className="text-sm text-muted-foreground">
                      This may take a moment for large imports...
                    </p>
                  </div>
                </div>
                <Progress value={importProgress} />
              </div>
            </CardContent>
          </Card>
        )}

        {/* Complete Step */}
        {step === "complete" && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <CheckCircle2 className="h-6 w-6 text-[var(--success)]" />
                Import Complete
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <p>Your transactions have been imported successfully!</p>
                {importResult && (
                  <div className="flex gap-4 text-sm text-muted-foreground">
                    <span>{importResult.imported} imported</span>
                    {importResult.skipped > 0 && (
                      <span>{importResult.skipped} duplicates skipped</span>
                    )}
                  </div>
                )}
                <Alert>
                  <Info className="h-4 w-4" />
                  <AlertDescription>
                    Some transactions may need review due to low confidence
                    categorization or unusual patterns.
                  </AlertDescription>
                </Alert>
                <div className="flex gap-2">
                  <Button onClick={() => setStep("review")}>
                    <AlertCircle className="mr-2 h-4 w-4" />
                    Review Flagged Transactions
                  </Button>
                  <Button variant="outline" onClick={resetImport}>
                    Import Another File
                  </Button>
                  <Button variant="outline" asChild>
                    <a href="/transactions">View All Transactions</a>
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Review Step */}
        {step === "review" && selectedCompanyId && (
          <div className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <AlertCircle className="h-6 w-6 text-[var(--warning)]" />
                  Review Flagged Transactions
                </CardTitle>
                <CardDescription>
                  These transactions need your attention due to low confidence
                  categorization, new patterns, or unusual amounts.
                </CardDescription>
              </CardHeader>
            </Card>
            <ReviewQueueContainer
              companyId={selectedCompanyId}
              onComplete={() => {
                toast.success("Review completed");
                setStep("complete");
              }}
            />
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => setStep("complete")}>
                Back to Summary
              </Button>
              <Button variant="outline" asChild>
                <a href="/transactions">View All Transactions</a>
              </Button>
            </div>
          </div>
        )}
      </div>

      {/* Column Mapping Dialog */}
      {showMappingDialog && detection && (
        <ColumnMappingDialog
          open={showMappingDialog}
          onOpenChange={setShowMappingDialog}
          headerRow={headerRow}
          previewRows={previewRows}
          suggestedMapping={suggestedMapping}
          onConfirm={mapping => {
            setShowMappingDialog(false);
            parseWithMapping(mapping, detection);
          }}
          onCancel={() => {
            setShowMappingDialog(false);
            resetImport();
          }}
        />
      )}

      {/* Smart Analysis Modal */}
      <SmartAnalysisModal
        open={showAnalysisModal}
        onOpenChange={setShowAnalysisModal}
        analyses={vendorAnalyses}
        onApprove={handleApproveVendors}
        isProcessing={upsertVendorMutation.isPending}
        onCorrect={analysis => {
          setCorrectionTarget(analysis);
          setShowCorrectionDialog(true);
        }}
      />

      {/* Vendor Correction Dialog */}
      {correctionTarget && (
        <VendorCorrectionDialog
          open={showCorrectionDialog}
          onOpenChange={setShowCorrectionDialog}
          companyId={selectedCompanyId!}
          transaction={{
            description: correctionTarget.description,
            amount: correctionTarget.amount / 100,
            date: correctionTarget.date,
          }}
          originalExtraction={{
            vendorName: correctionTarget.llmExtraction.vendor.name,
            productName: correctionTarget.llmExtraction.product.name,
            mwstRate: String(correctionTarget.llmExtraction.mwst.rate),
            confidence: correctionTarget.llmExtraction.confidence,
          }}
          onSuccess={() => {
            toast.success(
              "Correction submitted! The system will learn from this."
            );
            // Optionally re-run analysis
          }}
        />
      )}

      {/* Smart Analysis Tuning Dialog */}
      {selectedCompanyId && (
        <SmartAnalysisTuningDialog
          open={showTuningDialog}
          onOpenChange={setShowTuningDialog}
          companyId={selectedCompanyId}
          sessionId={sessionId}
          descriptions={extractedDescriptions}
          onApply={(toggles, results) => {
            // Store toggle preferences
            const prefs: Record<string, boolean> = {};
            toggles.forEach(t => {
              prefs[t.id] = t.enabled;
            });
            setTogglePreferences(prefs);
            toast.success(
              `Applied tuning: ${results.stats.vendorsExtracted} vendors extracted`
            );
          }}
        />
      )}

      {/* Post-Import Learning Dialog */}
      {selectedCompanyId && sessionId && importResult && (
        <PostImportLearningDialog
          open={showLearningDialog}
          onOpenChange={setShowLearningDialog}
          companyId={selectedCompanyId}
          sessionId={sessionId}
          formatLearning={{
            documentType:
              detection?.bankType && detection?.statementType
                ? `${detection.bankType}_${detection.statementType}`
                : "unknown",
            delimiter: detection?.delimiter || ",",
            encoding: detection?.encoding,
            dateFormat: "DD.MM.YYYY",
          }}
          togglePreferences={togglePreferences}
          importResults={{
            transactionsImported: importResult.imported,
            transactionsSkipped: importResult.skipped,
          }}
          isFirstImport={!preImportAnalysis?.learnedPattern}
          onComplete={decision => {
            toast.success(
              decision === "saved"
                ? "Learning saved! Future imports will be faster."
                : decision === "deferred"
                  ? "Reminder set to save learning later."
                  : "Proceeding without saving."
            );
          }}
        />
      )}
    </DashboardLayout>
  );
}
